/**
 * LaGift — quantity stepper
 * ---------------------------------------------------------------------------
 * The minus and plus buttons. The field itself is a plain `<input
 * type="number">` and stays one: this only presses the same buttons the browser
 * would have drawn, in a shape that exists on a touch screen and matches the
 * rest of the store.
 *
 * With the script gone the input still types, still validates and still
 * submits. The two buttons become inert, which is why they are `type="button"`
 * - a bare `<button>` inside a form defaults to `submit`, so without this they
 * would add the product to the cart instead of changing the number.
 */
/**
 * Scoped so the file can run more than once. A `<script src>` executes once per
 * tag, not once per URL, and a top-level class throws "already been declared"
 * on the second run. See CLAUDE.md.
 */
(() => {
  class LagiftQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-quantity-input]');
      this.steps = [...this.querySelectorAll('[data-quantity-step]')];

      this.steps.forEach((button) => {
        button.addEventListener('click', () => this.step(Number(button.dataset.quantityStep)));
      });

      // The number can also be typed, so the buttons' disabled state has to
      // follow the field rather than only follow itself.
      this.input?.addEventListener('input', () => this.syncButtons());
      this.syncButtons();
    }

    get min() {
      const value = Number(this.input?.min);
      return Number.isFinite(value) ? value : 1;
    }

    get max() {
      // An empty `max` is "no ceiling", and Number('') is 0 - which would pin
      // the field to its minimum and disable the plus button on every product
      // that does not limit its stock.
      const raw = this.input?.max;
      if (raw === undefined || raw === '') return Infinity;
      const value = Number(raw);
      return Number.isFinite(value) ? value : Infinity;
    }

    step(by) {
      if (!this.input) return;

      const current = Number(this.input.value) || this.min;
      const next = Math.min(this.max, Math.max(this.min, current + by));
      if (next === current) return;

      this.input.value = String(next);
      this.syncButtons();

      // `input.value = x` fires nothing. Anything listening for a quantity - the
      // cart page's line items, most obviously - hears this instead.
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncButtons() {
      const current = Number(this.input?.value) || this.min;
      this.steps.forEach((button) => {
        const by = Number(button.dataset.quantityStep);
        button.disabled = by < 0 ? current <= this.min : current >= this.max;
      });
    }
  }

  if (!customElements.get('lagift-quantity')) {
    customElements.define('lagift-quantity', LagiftQuantity);
  }
})();
